import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import "../styles/landing.css";

/* ─── Static Data ─────────────────────────────────────────── */

const FLOAT_CHARS = [
  "一","二","三","四","五","六","七","八","九",
  "●","◎","⊙","竹","索","萬","東","南","西","北","中","發","白",
];

const FEATURES = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <rect x="3" y="9" width="18" height="12" rx="2"/>
        <rect x="5" y="5" width="14" height="12" rx="2" fill="rgba(201,162,39,.12)"/>
        <rect x="7" y="2" width="10" height="12" rx="2" fill="rgba(201,162,39,.2)"/>
      </svg>
    ),
    title: "3D Layered Tiles",
    desc: "Beautifully rendered tiles stacked in multiple layers — see the depth, feel the strategy.",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <rect x="2" y="2" width="9" height="9" rx="1.5"/>
        <rect x="13" y="2" width="9" height="9" rx="1.5"/>
        <rect x="2" y="13" width="9" height="9" rx="1.5"/>
        <rect x="13" y="13" width="9" height="9" rx="1.5"/>
      </svg>
    ),
    title: "Multiple Tile Sets",
    desc: "Classic Mahjong, Animals, Fruits, Gems, Space, and more — each with hand-crafted art.",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
      </svg>
    ),
    title: "Power-Up System",
    desc: "Bomb tiles, wildcard matches, shuffle, and hints to overcome the toughest layouts.",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <rect x="3" y="4" width="18" height="18" rx="2"/>
        <path d="M16 2v4M8 2v4M3 10h18"/>
        <circle cx="8"  cy="15" r="1" fill="currentColor"/>
        <circle cx="12" cy="15" r="1" fill="currentColor"/>
        <circle cx="16" cy="15" r="1" fill="currentColor"/>
        <circle cx="8"  cy="19" r="1" fill="currentColor"/>
        <circle cx="12" cy="19" r="1" fill="currentColor"/>
      </svg>
    ),
    title: "Daily Rewards",
    desc: "Return every day to build streaks, unlock rare tile sets, and claim mystery bonuses.",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M3 6c0 5 3 8 9 10 6-2 9-5 9-10V4l-9-2-9 2v2z"/>
        <path d="M9 12l2 2 4-4"/>
      </svg>
    ),
    title: "Relax Mode",
    desc: "No timer, ambient sounds, and unlimited hints. Pure calm, pure focus.",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M8 21V9M12 21V3M16 21V13M4 21h16"/>
      </svg>
    ),
    title: "Challenge Mode",
    desc: "Race the clock on 500+ expert layouts. Climb the global leaderboard.",
  },
];

const BOARD_ROWS = [
  ["一","●","中","", "三","⊙","東","發"],
  ["", "二","●","白","", "竹","●","一"],
  ["中","", "三","●","發","", "二","●"],
  ["●","東","", "一","●","三","", "中"],
  ["", "⊙","發","", "二","●","東",""],
];

const TILE_SETS = [
  { name: "Classic", tiles: ["中","發","白","東"] },
  { name: "Animals", tiles: ["🐉","🐯","🐧","🐼"] },
  { name: "Fruits",  tiles: ["🍊","🍇","🍓","🍋"] },
  { name: "Gems",    tiles: ["💎","💍","🔮","⭐"] },
];

const POWERUPS = [
  { name: "Bomb",     desc: "Blast any blocked tile off the board instantly", color: "#c41e3a", emoji: "💣" },
  { name: "Wildcard", desc: "Match any tile regardless of its type",          color: "#c9a227", emoji: "⭐" },
  { name: "Shuffle",  desc: "Rearrange all free tiles for fresh matches",     color: "#7c3aed", emoji: "🔀" },
  { name: "Hint",     desc: "Highlights the best available matching pair",    color: "#2d8a4e", emoji: "💡" },
];

const STATS = [
  { end: 100,    suffix: "+", label: "Downloads" },
  { end: 2500,   suffix: "+", label: "Handcrafted Levels" },
  { end: 100,    suffix: "+", label: "3D Journeys" },
  { end: 50,     suffix: "+", label: "Tile Designs" },
];

const TESTIMONIALS = [
  { text: "Finally a Mahjong game that actually looks beautiful. The tile animations are perfection.", name: "MahjongMaster_99" },
  { text: "The daily rewards kept me coming back every single day. I am on a 47-day streak!", name: "TileLady_2024" },
  { text: "Challenge mode is insanely addictive. I missed my bus stop three times this week.", name: "PuzzleKing_J" },
];

/* ─── Phone Screen Content Components ────────────────────── */

const HomeScreen = () => (
  <div className="sc sc-home">
    <div className="sc-home-logo">Mahjong</div>
    <div className="sc-home-sub">Master</div>
    <div className="sc-home-tiles">
      {["中","發","白","東"].map((c, i) => (
        <div key={i} className="sc-mini-tile">{c}</div>
      ))}
    </div>
    <div className="sc-home-btns">
      <div className="sc-btn sc-btn-main">▶ Play</div>
      <div className="sc-btn sc-btn-sec">Daily</div>
      <div className="sc-btn sc-btn-sec">Shop</div>
    </div>
  </div>
);

const GameScreen = () => (
  <div className="sc sc-game">
    <div className="sc-game-hdr">
      <span>Level 24</span>
      <span>⏱ 02:34</span>
    </div>
    <div className="sc-board">
      {BOARD_ROWS.map((row, ri) => (
        <div key={ri} className="sc-row">
          {row.map((cell, ci) =>
            cell ? (
              <div key={ci} className={`sc-tile${ci === 2 && ri === 0 ? " sc-lit" : ""}`}>{cell}</div>
            ) : (
              <div key={ci} className="sc-empty" />
            )
          )}
        </div>
      ))}
    </div>
    <div className="sc-game-ftr">
      {["💣","⭐","🔀","💡"].map((e, i) => (
        <div key={i} className="sc-pu-btn">{e}</div>
      ))}
    </div>
  </div>
);

const TileScreen = () => (
  <div className="sc sc-tiles">
    <div className="sc-sec-title">Tile Sets</div>
    {TILE_SETS.map((set, i) => (
      <div key={i} className={`sc-set-row${i === 1 ? " sc-set-active" : ""}`}>
        <span className="sc-set-name">{set.name}</span>
        <div className="sc-set-preview">
          {set.tiles.map((t, j) => <span key={j}>{t}</span>)}
        </div>
        {i === 1 && <span className="sc-active-badge">Active</span>}
      </div>
    ))}
  </div>
);

const WinScreen = () => (
  <div className="sc sc-win">
    <div className="sc-win-stars">★★★</div>
    <div className="sc-win-title">Level Clear!</div>
    <div className="sc-win-score">Score: 8,240</div>
    <div className="sc-win-coins">+ 120 🪙</div>
    <div className="sc-btn sc-btn-main" style={{ marginTop: "14px" }}>Next Level</div>
  </div>
);

const RewardScreen = () => (
  <div className="sc sc-reward">
    <div className="sc-sec-title">Day 7 Streak!</div>
    <div className="sc-streak-row">
      {["M","T","W","T","F","S","S"].map((d, i) => (
        <div key={i} className={`sc-sd${i < 6 ? " sc-sd-done" : ""}`}>
          <div className="sc-sd-circle">{i < 6 ? "✓" : d}</div>
          <span>{d}</span>
        </div>
      ))}
    </div>
    <div className="sc-reward-box">
      <div className="sc-chest">🎁</div>
      <div className="sc-reward-lbl">Rare Tile Set</div>
    </div>
    <div className="sc-btn sc-btn-main" style={{ marginTop: "10px" }}>Claim!</div>
  </div>
);

const PhoneFrame = ({ children, label }) => (
  <div className="phone-wrap">
    <div className="phone-frame">
      <div className="phone-notch" />
      <div className="phone-screen">{children}</div>
    </div>
    {label && <p className="phone-label">{label}</p>}
  </div>
);

/* ─── Main Component ──────────────────────────────────────── */

const LandingPage = () => {
  const [scrolled,  setScrolled]  = useState(false);
  const [menuOpen,  setMenuOpen]  = useState(false);
  const [statVals,  setStatVals]  = useState(STATS.map(() => 0));
  const [statsDone, setStatsDone] = useState(false);
  const tilesRef = useRef(null);
  const statsRef = useRef(null);

  const storeLink =
    "https://play.google.com/store/apps/details?id=com.corvas.mahjong";

  /* Nav scroll shadow */
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  /* Floating tile background generator */
  useEffect(() => {
    const container = tilesRef.current;
    if (!container) return;
    const created = [];
    for (let i = 0; i < 26; i++) {
      const el = document.createElement("div");
      el.className = "ft";
      el.textContent = FLOAT_CHARS[Math.floor(Math.random() * FLOAT_CHARS.length)];
      el.style.left = `${Math.random() * 100}%`;
      el.style.top  = `${Math.random() * 100}%`;
      el.style.setProperty("--dur",   `${10 + Math.random() * 18}s`);
      el.style.setProperty("--delay", `${-(Math.random() * 28)}s`);
      el.style.setProperty("--op",    `${0.04 + Math.random() * 0.13}`);
      el.style.setProperty("--rot",   `${Math.random() * 40 - 20}deg`);
      el.style.setProperty("--sc",    `${0.55 + Math.random() * 0.9}`);
      container.appendChild(el);
      created.push(el);
    }
    return () => created.forEach((el) => el.remove());
  }, []);

  /* Scroll-reveal observer */
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => e.isIntersecting && e.target.classList.add("revealed")),
      { threshold: 0.12 }
    );
    document.querySelectorAll(".reveal").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  /* Stats counter animation */
  useEffect(() => {
    if (!statsRef.current) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || statsDone) return;
        setStatsDone(true);
        STATS.forEach((stat, idx) => {
          const frames = 70;
          let f = 0;
          const iv = setInterval(() => {
            f++;
            const ease = 1 - Math.pow(1 - f / frames, 3);
            setStatVals((prev) => {
              const next = [...prev];
              next[idx] = stat.decimal
                ? parseFloat((stat.end * ease).toFixed(1))
                : Math.floor(stat.end * ease);
              return next;
            });
            if (f >= frames) clearInterval(iv);
          }, 2000 / frames);
        });
      },
      { threshold: 0.4 }
    );
    io.observe(statsRef.current);
    return () => io.disconnect();
  }, [statsDone]);

  const PlayStoreBtn = ({ large = false }) => (
    <a
      href={storeLink}
      target="_blank"
      rel="noreferrer"
      className={`ps-btn${large ? " ps-btn-lg" : ""}`}
    >
      <svg className="ps-icon" viewBox="0 0 22.773 24" fill="currentColor">
        <path d="M.227 23.641a1.97 1.97 0 0 1-.227-.963V1.322A1.97 1.97 0 0 1 .227.681L.34.57 12.61 12 .34 23.43z"/>
        <path d="M16.953 15.985l-3.23-1.878-2.5 2.5 3.9 3.9a1.322 1.322 0 0 0 1.83-.072v-4.45z"/>
        <path d="M20.847 10.286 17.01 8.13l-3.23 3.23 3.23 3.23 3.838-1.836a1.322 1.322 0 0 0 0-2.468z"/>
        <path d="M13.61.756.34.57l-.113.111 12.27 12.27 2.5-2.5z"/>
      </svg>
      <div className="ps-text">
        <span className="ps-top">GET IT ON</span>
        <span className="ps-main">Google Play</span>
      </div>
    </a>
  );

  return (
    <div className="mj-root">
      {/* Global floating tile background */}
      <div ref={tilesRef} className="tiles-global" aria-hidden="true" />

      {/* ── NAV ── */}
      <nav className={`mj-nav${scrolled ? " scrolled" : ""}`}>
        <div className="nav-inner">
          <div className="nav-logo">
            <div className="nav-logo-tile">中</div>
            <span>Mahjong Blast</span>
          </div>

          <div className={`nav-links${menuOpen ? " open" : ""}`}>
            <a href="#features"    onClick={() => setMenuOpen(false)}>Features</a>
            <a href="#how-to-play" onClick={() => setMenuOpen(false)}>How to Play</a>
            <a href="#gallery"     onClick={() => setMenuOpen(false)}>Gallery</a>
            <PlayStoreBtn />
          </div>

          <button
            className={`nav-ham${menuOpen ? " open" : ""}`}
            onClick={() => setMenuOpen((p) => !p)}
            aria-label="Toggle menu"
          >
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="mj-hero" id="hero">
        <div className="hero-bg" />
        <div className="hero-inner">
          <div className="hero-text reveal">
            <div className="hero-badge">
              <span className="hero-badge-tile">中</span>
              Premium Mahjong Puzzle Game
            </div>
            <h1 className="hero-h1">
              Relax, Match &amp;<br />
              <span className="gold">Master the Tiles</span>
            </h1>
            <p className="hero-sub">
              The most beautiful Mahjong puzzle game on Android. Clear layered
              tiles, unlock rewards, and achieve true Zen mastery.
            </p>
            <div className="hero-ctas">
              <PlayStoreBtn large />
              <a href="#how-to-play" className="ghost-btn">
                How to Play
                <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8 3v10M3 8l5 5 5-5" />
                </svg>
              </a>
            </div>
            <div className="hero-rating">
              <span className="stars">★★★★★</span>
              <span>4.8 · 100K+ Downloads</span>
            </div>
          </div>

          <div className="hero-visual reveal">
            <div className="hero-glow-ring" />
            <PhoneFrame><HomeScreen /></PhoneFrame>
            <div className="chip chip-1"><div className="mj-tile mj-dragon-red  mj-sm"><span>中</span></div></div>
            <div className="chip chip-2"><div className="mj-tile mj-dragon-green mj-sm"><span>發</span></div></div>
            <div className="chip chip-3"><div className="mj-tile mj-dragon-white mj-sm"><span>白</span></div></div>
            <div className="chip chip-4"><div className="mj-tile mj-wind        mj-sm"><span>東</span></div></div>
          </div>
        </div>

        <a href="#preview" className="scroll-hint" aria-label="Scroll down">
          <span>Scroll</span>
          <div className="scroll-arrow" />
        </a>
      </section>

      {/* ── GAME PREVIEW ── */}
      <section className="mj-section preview-sec" id="preview">
        <div className="sec-inner">
          <div className="sec-head reveal">
            <p className="sec-label">Gameplay</p>
            <h2>A Beautiful Puzzle <span className="gold">Experience</span></h2>
            <p className="sec-desc">
              Three-dimensional tile towers, smooth matching animations, and
              deeply satisfying clear effects.
            </p>
          </div>

          <div className="preview-phones reveal">
            <PhoneFrame label="Game Board">   <GameScreen />  </PhoneFrame>
            <PhoneFrame label="Tile Sets">    <TileScreen />  </PhoneFrame>
            <PhoneFrame label="Level Clear!"> <WinScreen />   </PhoneFrame>
          </div>

          <div className="preview-pills reveal">
            {[
              ["⊞", "3D Layered Tile Stacks"],
              ["✦", "Smooth Match Animations"],
              ["◈", "50+ Unique Tile Designs"],
              ["⚡", "Instant Responsive Touch"],
            ].map(([icon, text], i) => (
              <div key={i} className="pill">
                <span className="pill-icon">{icon}</span>
                {text}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className="mj-section features-sec" id="features">
        <div className="sec-inner">
          <div className="sec-head reveal">
            <p className="sec-label">Features</p>
            <h2>Everything You Need to <span className="gold">Master Mahjong</span></h2>
          </div>
          <div className="features-grid">
            {FEATURES.map((f, i) => (
              <div key={i} className="feat-card reveal" style={{ animationDelay: `${i * 0.08}s` }}>
                <div className="feat-icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── REWARDS ── */}
      <section className="mj-section rewards-sec" id="rewards">
        <div className="sec-inner">
          <div className="sec-head reveal">
            <p className="sec-label">Rewards &amp; Power-Ups</p>
            <h2>Come Back Every Day for <span className="gold">Treasures</span></h2>
          </div>

          <div className="rewards-layout">
            <div className="rewards-left reveal">
              <div className="reward-card streak-card">
                <div className="streak-hdr">
                  <span className="streak-fire">🔥</span>
                  <div>
                    <h3>Daily Streak</h3>
                    <p>Play every day to unlock bigger and rarer rewards</p>
                  </div>
                </div>
                <div className="streak-days">
                  {["M","T","W","T","F","S","S"].map((d, i) => (
                    <div key={i} className={`s-day${i < 5 ? " s-done" : i === 5 ? " s-today" : ""}`}>
                      <div className="s-circle">{i < 5 ? "✓" : i === 5 ? "★" : d}</div>
                      <span>{d}</span>
                    </div>
                  ))}
                </div>
                <div className="streak-cta">Day 6 — Claim Your Streak Bonus!</div>
              </div>

              <div className="reward-card daily-card">
                <div className="daily-icon">🎁</div>
                <h3>Daily Mystery Gift</h3>
                <p>Open a surprise box every 24 hours — coins, power-ups, or a rare tile set!</p>
                <div className="daily-chips">
                  {["🪙","💣","⭐","🔮"].map((e, i) => (
                    <div key={i} className="daily-chip">{e}</div>
                  ))}
                </div>
              </div>
            </div>

            <div className="rewards-right reveal">
              <h3 className="pu-title">Power-Ups</h3>
              <div className="pu-grid">
                {POWERUPS.map((p, i) => (
                  <div key={i} className="pu-card" style={{ "--pu": p.color }}>
                    <div className="pu-emoji">{p.emoji}</div>
                    <div className="pu-info">
                      <h4>{p.name}</h4>
                      <p>{p.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW TO PLAY ── */}
      <section className="mj-section htp-sec" id="how-to-play">
        <div className="sec-inner">
          <div className="sec-head reveal">
            <p className="sec-label">How to Play</p>
            <h2>Simple to Learn, <span className="gold">Deep to Master</span></h2>
          </div>

          <div className="htp-steps">
            {[
              { n: "01", icon: "⊙", title: "Find Free Tiles",       desc: "A tile is free when its left or right side is open AND its top is not covered. Free tiles glow with a golden outline." },
              { n: "02", icon: "↔", title: "Match Identical Pairs", desc: "Tap two identical free tiles to remove them from the board. Each match creates a satisfying burst of light." },
              { n: "03", icon: "✓", title: "Clear the Board",       desc: "Remove every pair to complete the level. Plan carefully — some matches reveal new possibilities beneath." },
            ].map((step, i) => (
              <div key={i} className="htp-card reveal" style={{ animationDelay: `${i * 0.12}s` }}>
                <div className="htp-num">{step.n}</div>
                <div className="htp-icon">{step.icon}</div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </div>
            ))}
          </div>

          <div className="tileset-row reveal">
            <h3>Choose Your Tile Set</h3>
            <div className="tileset-cards">
              {TILE_SETS.map((set, i) => (
                <div key={i} className={`ts-card${i === 0 ? " ts-selected" : ""}`}>
                  <div className="ts-tiles">
                    {set.tiles.map((t, j) => <span key={j}>{t}</span>)}
                  </div>
                  <span className="ts-name">{set.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── GALLERY ── */}
      <section className="mj-section gallery-sec" id="gallery">
        <div className="sec-inner">
          <div className="sec-head reveal">
            <p className="sec-label">Gallery</p>
            <h2>See the <span className="gold">Whole Experience</span></h2>
          </div>
        </div>
        <div className="gallery-scroll">
          {[
            { screen: <HomeScreen />,   label: "Home Screen"    },
            { screen: <GameScreen />,   label: "Game Board"     },
            { screen: <TileScreen />,   label: "Tile Selection" },
            { screen: <WinScreen />,    label: "Victory Screen" },
            { screen: <RewardScreen />, label: "Daily Rewards"  },
          ].map(({ screen, label }, i) => (
            <div key={i} className="gallery-item reveal" style={{ animationDelay: `${i * 0.07}s` }}>
              <PhoneFrame label={label}>{screen}</PhoneFrame>
            </div>
          ))}
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="mj-section stats-sec" id="stats" ref={statsRef}>
        <div className="sec-inner">
          <div className="sec-head reveal">
            <p className="sec-label">By the Numbers</p>
            <h2>Loved by <span className="gold">Players Worldwide</span></h2>
          </div>

          <div className="stats-grid">
            {STATS.map((stat, i) => (
              <div key={i} className="stat-card reveal">
                <div className="stat-val">{statVals[i]}{stat.suffix}</div>
                <div className="stat-lbl">{stat.label}</div>
              </div>
            ))}
          </div>

          <div className="testimonials reveal">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="testi-card">
                <div className="testi-stars">★★★★★</div>
                <p className="testi-text">{t.text}</p>
                <span className="testi-name">— {t.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="mj-section cta-sec" id="download">
        <div className="cta-tiles" aria-hidden="true">
          {["中","發","白","東","南","西","北","一","二","三","●","◎"].map((c, i) => (
            <div key={i} className={`cta-ft cta-ft-${i}`}>{c}</div>
          ))}
        </div>
        <div className="cta-inner reveal">
          <h2>Ready to Find Your <span className="gold">Zen?</span></h2>
          <p>
            Download free on Google Play. No ads during gameplay. No
            paywalls on core features. Just pure Mahjong.
          </p>
          <PlayStoreBtn large />
          <p className="cta-note">Free · Android · No account needed</p>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="mj-footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <div className="footer-logo">
              <div className="nav-logo-tile sm">中</div>
              <span>Mahjong Blast</span>
            </div>
            <p>The most beautiful Mahjong puzzle experience on Android.</p>
            <p className="footer-ver">v2.4.1 · © 2026 Corvas Studio</p>
          </div>

          <div className="footer-cols">
            <div className="fcol">
              <h4>Game</h4>
              <a href="#features">Features</a>
              <a href="#how-to-play">How to Play</a>
              <a href="#gallery">Gallery</a>
              <a href="#download">Download</a>
            </div>
            <div className="fcol">
              <h4>Legal</h4>
              <Link to="/privacy">Privacy Policy</Link>
              <Link to="/terms">Terms of Use</Link>
              <a href="mailto:corvas.official@gmail.com">Contact</a>
            </div>
            <div className="fcol">
              <h4>More Apps</h4>
              <a href="https://play.google.com/store/apps/details?id=com.corvas.reactioncam" target="_blank" rel="noreferrer">ReactionCam</a>
              <a href="https://play.google.com/store/apps/details?id=com.corvas.pencilate" target="_blank" rel="noreferrer">Pencilate</a>
              <a href="https://play.google.com/store/apps/details?id=com.corvas.behindtext" target="_blank" rel="noreferrer">BehindText</a>
            </div>
          </div>
        </div>
        <div className="footer-btm">
          <p>Made with ♥ for Mahjong lovers everywhere</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
